<!--
Componente per la gestione delle attività di un paziente

Parametri che si ricevono dalla vista dove viene richiamato il componente

    PARAMETRO                                 TIPOLOGIA         REQUIRED      DEFAULT             DESCRIZIONE
    modal_id                                  String            FALSE         gap_modal           ID con cui viene richiamato il modale
    business_unit_id                          Number            TRUE          no default          ---
    is_super_user                             Boolean           FALSE         false               ---
    adt_pattern_matchings                     String            FALSE         no default          Pattern matchings della business_unit di riferimento
    cod_struct                                String            TRUE          no default          Codice struttura ADT della currrent_bu
    mpi_disable_search_id_mpi                 Boolean           FALSE         false               Disabilita la ricerca per ID MPI per gli utenti non superuser
    mpi_additional_search_fields              Array             FALSE         no default          Campi di ricerca aggiuntivi abilitati
    open_modal_on_load                        Boolean           FALSE         false               Apre in automatico la modale di ricerca
    send_data_through_event                   Boolean           FALSE         true                Se messo a true il pulsante + emette un evento invece che rimandare alla pagina di creazione guest
    modal_title                               String            FALSE         ''                  Titolo della modale, temporaneo finchè non si sviluppa l'interfaccia ADT
-->

<template>
  <div>
    <spinner :visible.sync="this.loading" :overlay="true"></spinner>
    <div
      aria-hidden="true"
      :aria-labelledby="modal_id"
      data-backdrop="static"
      data-keyboard="false"
      class="modal fade hide"
      v-bind:style="modal_style"
      :id="modal_id"
      role="dialog"
      style="min-width: 680px"
    >
      <div class="modal-header">
        <h3 class="title">
          <span class="fa-light fa-magnifying-glass"></span>
          {{ modal_title }}
          <button @click="closeModal()" aria-hidden="true" class="close" data-dismiss="modal" type="button">
            <i class="fa-light fa-xmark"></i>
          </button>
        </h3>
        <div class="text-danger" v-if="this.error_msg">
          {{ this.error_msg }}
        </div>
      </div>
      <div class="modal-body form-horizontal" v-bind:style="modal_body_style">
        <div class="row-fluid">
          <div :class="show_search_results ? 'span4' : 'span12'" id="span_search_form">
            <div class="control-group">
              <label class="control-label" for="visit_number">{{ i18nJS.visit_number }}</label>
              <div>
                <input
                  type="text"
                  id="visit_number"
                  class="form-control input-medium"
                  v-model="visit_number"
                  @keyup.enter="searchOnADT()"
                />
              </div>
            </div>
            <div class="control-group">
              <label class="control-label">{{ i18nJS.unit_label }}</label>
              <div>
                <v-select2
                  @update:element_data="updateCodiceAdtReparto"
                  :key="updateAdt"
                  id="unit_label"
                  :data-ajax--url="'it/business_units/autocomplete_business_unit_nuclei_adt'"
                  :data-placeholder="'Seleziona reparto'"
                  data-allow-clear="false"
                />
              </div>
            </div>
            <div class="control-group">
              <label class="control-label">{{ i18nJS.admission_date }}</label>
              <div>
                <datepicker :value="admission_date" @input="(val) => (admission_date = val)" id="admission_date" />
              </div>
            </div>
            <div class="control-group">
              <label class="control-label" for="surname">{{ i18nJS.surname }}</label>
              <div>
                <input type="text" id="surname" class="form-control input-medium" v-model="surname" @keyup.enter="searchOnADT()" />
              </div>
            </div>
            <div class="control-group">
              <label class="control-label" for="firstname">{{ i18nJS.firstname }}</label>
              <div>
                <input type="text" id="firstname" class="form-control input-medium" v-model="firstname" @keyup.enter="searchOnADT()" />
              </div>
            </div>
            <div class="control-group">
              <label class="control-label" for="gender">{{ i18nJS.gender }}</label>
              <div>
                <input type="checkbox" v-model="gender_male" />
                {{ i18nJS.gender_male_extended }}
                <input type="checkbox" v-model="gender_female" />
                {{ i18nJS.gender_female_extended }}
              </div>
            </div>
            <div class="control-group">
              <label class="control-label">{{ i18nJS.birthdate }}</label>
              <div>
                <datepicker :value="birthdate" @input="(val) => (birthdate = val)" id="birthdate_adt" />
              </div>
            </div>
            <div v-if="mpi_additional_search_fields.includes('enable_search_birthplace')">
              <label class="control-label">{{ i18nJS.birthplace }}</label>
              <div>
                <v-select2
                  v-model="birthplace"
                  id="birthplace"
                  :data-allow-clear="true"
                  :key="upgrade"
                  data-url="/it/countries/country_from_city.json"
                  data-ajax--url="/it/countries/autocomplete_city"
                />
              </div>
            </div>
            <div class="control-group">
              <label class="control-label" for="cf">{{ i18nJS.cf }}</label>
              <div>
                <input type="text" id="cf" class="form-control input-medium" v-model="cf" @keyup.enter="searchOnADT()" />
                <span v-if="this.cf_error_format" class="text-danger">{{ i18nJS.errors.wrong_format }}</span>
                <span v-if="this.cf_error_max_length" class="text-danger">{{ i18nJS.errors.wrong_max_length }}</span>
                <span v-if="this.cf_error_min_length" class="text-danger"
                  >{{ i18nJS.errors.wrong_min_length }}: {{ Math.abs(16 - this.cf.length) }}</span
                >
              </div>
            </div>
            <div class="control-group" v-if="external_ids.length > 0">
              <label class="control-label" :for="external_ids[0]">{{
                external_ids.length == 1 ? i18nJS[external_ids[0]] : i18nJS.external_anagraphic_id
              }}</label>
              <v-select2
                v-if="external_ids.length > 1"
                v-model="external_id_code"
                class="input-medium js-tooltip"
                :key="upgrade"
                :title="i18nJS.clic_per_selezionare"
                :options-list="externalIdsOptionsList"
                id="select2-consent-status"
                :data-allow-clear="true"
                :data-placeholder="`${i18nJS.clic_per_selezionare} `"
                @input="external_ids[0]"
              >
              </v-select2>
              <span :class="'control'">
                <input
                  type="text"
                  id="external_id_value"
                  class="form-control input-medium"
                  :disabled="external_id_code == null || external_id_code == ''"
                  v-model="external_id_value"
                  @keyup.enter="searchOnADT()"
                />
              </span>
            </div>
            <div class="control-group" v-if="identity_cards.length > 0">
              <label class="control-label" :for="identity_cards[0]">{{
                identity_cards.length == 1 ? i18nJS[identity_cards[0]] : i18nJS.identity_card
              }}</label>
              <v-select2
                v-if="identity_cards.length > 1"
                v-model="identity_card_code"
                class="input-medium js-tooltip"
                :key="upgrade"
                :title="i18nJS.clic_per_selezionare"
                :options-list="identityCardsOptionsList"
                id="select2-consent-status"
                :data-allow-clear="true"
                :data-placeholder="`${i18nJS.clic_per_selezionare} `"
                @input="identity_cards[0]"
              >
              </v-select2>
              <span :class="'control' + (identity_card_code != null && identity_card_code != '' ? ' obbligatorio' : '')">
                <input
                  type="text"
                  id="identity_card_value"
                  class="form-control input-medium mychzn-obbligatorio"
                  :disabled="identity_card_code == null || identity_card_code == ''"
                  v-model="identity_card_value"
                  @keyup.enter="searchOnADT()"
                />
              </span>
            </div>
            <br />
            <div>
              <label>Campi di ricerca minimi</label>
              <div>
                <ul>
                  <li v-for="pm in patternMatchings" v-bind:key="pm.id" style="display: inline-block; margin-right: 5px">
                    {{ pm.description }} -
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div
            v-if="show_search_results"
            class="span8"
            id="span_search_results"
            :style="this.external_ids.length + this.identity_cards.length > 0 ? 'margin-left: 460px' : ''"
          >
            <table class="table table-hover" id="search_results">
              <thead class="sticky-on-top">
                <th scope="col" class="heading"></th>
                <th scope="col" class="heading">{{ i18nJS.visit_number }}</th>
                <th scope="col" class="heading">{{ i18nJS.fullname }}</th>
                <th scope="col" class="heading">{{ i18nJS.birthdate }}</th>
                <th scope="col" class="heading"></th>
              </thead>
              <tbody>
                <tr v-for="item in search_results" v-bind:key="item.id">
                  <td scope="row">
                    <div class="dropdown">
                      <a href="#" class="dropbtn"><span class="fa-light fa-circle-info"></span></a>
                      <div class="dropdown-content">
                        <ul>
                          <li v-for="info in additionalInfo(item)" v-bind:key="info.id">
                            {{ info.message }}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </td>
                  <td scope="row">
                    {{ item.visit_number }}
                    <div class="bugiardino_link royalblue">{{ item.admission_datetime }}</div>
                  </td>
                  <td scope="row">
                    {{ item.surname + ' ' + item.firstname }}
                    <div class="bugiardino_link royalblue">{{ item.cf }}</div>
                  </td>
                  <td scope="row">
                    {{ item.birthdate }}
                    <div v-if="item.age" class="bugiardino_link royalblue">
                      {{ item.age + ' ' + (item.age == '1' ? i18nJS.year : i18nJS.years) }}
                    </div>
                  </td>
                  <td v-if="!send_data_through_event" scope="row">
                    <a :href="urlForImportAdmission(item)" class="fa-light fa-plus pulsante_comando"></a>
                  </td>
                  <td v-if="send_data_through_event" scope="row">
                    <a @click="sendDataThroughEvent(item)" class="fa-light fa-plus pulsante_comando"></a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="modal-footer" style="position: relative; z-index: 666">
        <template>
          <button @click="cleanSearchFilters()" class="btn btn-primary">{{ i18nJS.clean_search }}</button>
          <button :disabled="!action_disable" @click="searchOnADT()" class="btn btn-primary">{{ i18nJS.search }}</button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { LabelApi } from '@/epersonam/mixins/labels.js';
import apiConnector from '@/epersonam/apiConnector';
import datepicker from '../datepicker';
import spinner from '../spinner';
import vSelect2 from '../vSelect2';

export default {
  name: 'adtSearch',
  mixins: [LabelApi],
  props: {
    modal_id: { type: String },
    business_unit_id: { type: Number, required: true },
    is_super_user: { type: Boolean, default: false },
    adt_pattern_matchings: { type: String },
    cod_struct: { type: String, required: true },
    mpi_disable_search_id_mpi: { type: Boolean, default: false },
    mpi_additional_search_fields: { type: Array },
    open_modal_on_load: { type: Boolean, default: false },
    send_data_through_event: { type: Boolean, default: true },
    modal_title: { type: String, default: '' },
  },
  components: {
    datepicker,
    spinner,
    vSelect2,
  },

  data() {
    return {
      firstname: '',
      surname: '',
      gender_male: false,
      gender_female: false,
      birthdate: '',
      birthplace: '',
      cf: '',

      external_id_code: null,
      external_id_value: null,
      identity_card_code: null,
      identity_card_value: null,

      visit_number: null,
      admission_date: null,

      search_results: [],

      search_done: false,
      searched_by_cf: false,

      error_msg: null,
      loading: false,
      upgrade: true,

      cf_regexp: new RegExp('^[a-zA-Z]{6,}[0-9]{2,}[a-zA-Z]+[0-9]{2,}[a-zA-Z]+[0-9]{3,}[a-zA-Z]+$'),
      cf_error_format: false,
      cf_error_min_length: false,
      cf_error_max_length: false,

      unit_label: null,
      unit_id: 0,
      unit_text: '',
      updateAdt: 'updateAdt+1',

      base_url: window.config.ePersonamService.ePersonamHost + '/' + window.locale,
    };
  },
  watch: {
    gender_male: function () {
      if (this.gender_male) {
        this.gender_female = false;
      }
    },
    gender_female: function () {
      if (this.gender_female) {
        this.gender_male = false;
      }
    },
    cf: function () {
      this.searched_by_cf = false;
      if (this.cf == null || this.cf.length == 0) {
        this.cf_error_format = false;
        this.cf_error_min_length = false;
        this.cf_error_max_length = false;
      } else if (this.cf.length > 16) {
        this.cf_error_max_length = true;
      } else if (this.cf?.length < 16) {
        this.cf_error_min_length = true;
        this.cf_error_format = false;
      } else {
        this.cf_error_format = !this.cf_regexp.test(this.cf);
        this.cf_error_min_length = false;
        this.cf_error_max_length = false;
      }
    },
  },
  computed: {
    action_disable() {
      return !this.cf_error_min_length && !this.cf_error_max_length && !this.cf_error_format;
    },
    search_id_mpi_enabled() {
      return this.is_super_user || !this.mpi_disable_search_id_mpi;
    },
    external_ids() {
      let ids = [];

      if (this.search_id_mpi_enabled) {
        ids[ids.length] = 'id_mpi';
      }

      if (this.mpi_additional_search_fields.includes('enable_search_id_aur')) {
        ids[ids.length] = 'id_aur';
      }

      return ids;
    },
    externalIdsOptionsList() {
      let ol = [];

      for (let i = 0; i < this.external_ids.length; i++) {
        ol[ol.length] = [i18nJS[this.external_ids[i]], this.external_ids[i]];
      }

      return ol;
    },
    identity_cards() {
      let cards = [];

      // ["enable_search_birthplace","enable_search_id_aur","enable_search_tessera_sanitaria","enable_search_stp_code","enable_search_eni_code","enable_search_team_code"]
      let cards_list = ['tessera_sanitaria', 'stp_code', 'eni_code', 'team_code'];

      for (let i = 0; i < cards_list.length; i++) {
        if (this.mpi_additional_search_fields.includes('enable_search_' + cards_list[i])) {
          cards[cards.length] = cards_list[i];
        }
      }

      return cards;
    },
    identityCardsOptionsList() {
      let ol = [];

      for (let i = 0; i < this.identity_cards.length; i++) {
        ol[ol.length] = [i18nJS[this.identity_cards[i]], this.identity_cards[i]];
      }

      return ol;
    },
    gender() {
      if (this.gender_male) {
        return 'M';
      } else if (this.gender_female) {
        return 'F';
      } else {
        return null;
      }
    },
    show_search_results() {
      return this.search_results.length > 0;
    },
    modal_style() {
      return this.show_search_results
        ? {
            'min-width': '80vw !important',
            'margin-left': '-40vw !important',
          }
        : {};
    },
    modal_body_height() {
      return 400 + (this.external_ids.length > 0 ? 50 : 0) + (this.identity_cards.length > 0 ? 50 : 0);
    },
    modal_body_style() {
      return { 'min-height': this.modal_body_height + 'px' };
    },
    pageCount() {
      return this.search_results.length;
    },
    patternMatchings() {
      let pmList = [];
      let id = 0;

      let pmSplit = this.adt_pattern_matchings.split('~');
      for (var i = 0; i < pmSplit.length; i++) {
        let tmp = [];

        if (!this.search_id_mpi_enabled && pmSplit[i] == 'id_mpi') {
          continue;
        }

        let singlePmSplit = pmSplit[i].split('^');
        for (var j = 0; j < singlePmSplit.length; j++) {
          tmp.push(i18nJS[singlePmSplit[j]]);
        }

        pmList.push({ id: id++, description: tmp.join(', ') });
      }

      return pmList;
    },
  },
  async beforeMount() {
    this.neededlabels = ['business_unit_id'];
    await this.setupLabels();
  },
  mounted() {
    if (this.external_ids.length == 1) {
      this.external_id_code = this.external_ids[0];
    }

    if (this.identity_cards.length == 1) {
      this.identity_card_code = this.identity_cards[0];
    }

    if (this.open_modal_on_load) {
      $('#adt_search_modal').modal('show');
    }
  },
  methods: {
    updateCodiceAdtReparto(data) {
      this.unit_id = data.id;
      this.unit_text = data.text;
      this.unit_label = data.cod_struct;
      // eslint-disable-next-line vue/no-mutating-props
      if (!!data.cod_struct_parent) this.cod_struct = data.cod_struct_parent;
    },
    sendDataThroughEvent(item) {
      this.closeModal();
      $('#adt_search_modal').modal('hide');
      item = {
        ...item,
        unit_id: this.unit_id,
        unit_text: this.unit_text,
      };
      eventBus.$emit('admissionSentFromADT', item);
    },
    openModal() {
      $('#adt_search_modal').modal('show');
    },
    closeModal() {
      $('#adt_search_modal').modal('hide');
      this.cleanSearchFilters();
      this.cleanSearchResults();
    },
    cleanSearchFilters() {
      this.search_done = false;

      this.visit_number = null;
      this.admission_date = null;
      this.surname = '';
      this.firstname = '';
      this.gender_male = false;
      this.gender_female = false;
      this.birthdate = null;
      this.cf = null;
      this.birthplace = null;
      this.upgrade = !this.upgrade;
      this.unit_label = null;
      this.updateAdt = this.updateAdt += 1;

      if (this.external_ids.length > 1) {
        this.external_id_code = null;
      }
      this.external_id_value = null;

      if (this.identity_cards.length > 1) {
        this.identity_card_code = null;
      }
      this.identity_card_value = null;
    },
    cleanSearchResults() {
      this.search_results = [];
    },
    searchOnADT() {
      this.error_msg = '';
      this.search_done = false;
      this.cleanSearchResults();
      this.loading = true;

      if (!(this.cod_struct && this.unit_label)) {
        this.error_msg = 'Si prega di selezionare un reparto';
        this.search_results = [];
        this.loading = false;
        return;
      }

      if (
        (this.external_ids.length > 1 &&
          this.external_id_code != null &&
          this.external_id_code != '' &&
          (this.external_id_value == null || this.external_id_value == '')) ||
        (this.identity_cards.length > 1 &&
          this.identity_card_code != null &&
          this.identity_card_code != '' &&
          (this.identity_card_value == null || this.identity_card_value == ''))
      ) {
        this.error_msg = i18nJS.fill_mandatory_fields_error;
        this.search_results = [];
        this.loading = false;
        return;
      }

      let params = {
        locale: window.locale,
        business_unit_id: this.business_unit_id,
        visit_number: this.visit_number,
        admission_date: this.admission_date,
        surname: String(this.surname).toUpperCase(),
        firstname: String(this.firstname).toUpperCase(),
        gender: this.gender,
        birthdate: this.birthdate,
        birthplace: this.birthplace,
        cf: String(this.cf).toUpperCase(),
        codice_adt_struttura: this.cod_struct,
        codice_adt_reparto: this.unit_label,
      };

      params[this.external_id_code] = this.external_id_value;
      params[this.identity_card_code] = this.identity_card_value;

      apiConnector()
        .get(this.base_url + '/adt/admissions/', { params: params })
        .then(({ data }) => {
          this.search_results = data;
          this.error_msg = null;
          this.loading = false;
          this.search_done = true;
        })
        .catch((err) => {
          switch (err.response.status) {
            case 400:
              this.error_msg = i18nJS.search_parameters_not_valid;
              this.search_results = [];
              break;
            case 422:
              this.error_msg = i18nJS.external_service_not_configured;
              this.search_results = [];
              this.search_done = true;
              break;
            default:
              this.error_msg = i18nJS.external_search_error;
              this.search_results = [];
              this.search_done = true;
              break;
          }

          this.loading = false;
        });
    },
    urlForImportAdmission(item) {
      let params = [];
      params = this.addParam(params, 'guest[surname]', item.surname);
      params = this.addParam(params, 'guest[firstname]', item.firstname);
      params = this.addParam(params, 'guest[gender]', this.genderNumeric(item.gender));
      params = this.addParam(params, 'guest[birthdate]', item.birthdate);
      params = this.addParam(params, 'guest[cf]', item.cf);
      params = this.addParam(params, 'guest[resaddress]', item.residence != null ? item.residence.street : null);
      params = this.addParam(params, 'guest[rescap]', item.residence != null ? item.residence.cap : null);
      params = this.addParam(params, 'guest[rescity_id]', item.residence != null ? item.residence.city_id : null);
      params = this.addParam(params, 'guest[domaddress]', item.domicile != null ? item.domicile.street : null);
      params = this.addParam(params, 'guest[domcap]', item.domicile != null ? item.domicile.cap : null);
      params = this.addParam(params, 'guest[domcity_id]', item.domicile != null ? item.domicile.city_id : null);
      params = this.addParam(params, 'guest[birthcity_id]', item.birthplace != null ? item.birthplace.city_id : null);
      params = this.addParam(params, 'guest[phone]', item.phone);
      params = this.addParam(params, 'guest[mobile]', item.mobile);
      params = this.addParam(params, 'guest[email]', item.email);
      params = this.addParam(params, 'nr_cartella', item.visit_number);
      params = this.addParam(params, 'data_accesso', item.admission_date);
      params = this.addParam(params, 'id_mpi', item.id_mpi);
      params = this.addParam(params, 'id_aur', item.id_aur);
      params = this.addParam(params, 'guest[codsan]', item.codsan);
      params = this.addParam(params, 'guest[rescountry_id]', item.rescountry_id);
      params = this.addParam(params, 'guest[asl_id]', item.asl_id);
      params = this.addParam(params, 'guest[distretto_id]', item.distretto_id);

      params = this.addParam(params, 'incomplete_guest', item.incomplete_guest);

      let uri = params.join('&');

      return '/' + window.locale + '/guests/new?' + uri;
    },
    addParam(params, key, value) {
      if (key != null && key != '' && value != null && value != '') {
        params.push(encodeURIComponent(key) + '=' + value);
      }

      return params;
    },
    genderNumeric(genderLiteral) {
      if (genderLiteral == 'M') {
        return 1;
      } else if (genderLiteral == 'F') {
        return 2;
      } else {
        return null;
      }
    },
    genderExtended(genderLiteral) {
      if (genderLiteral == 'M') {
        return i18nJS.gender_male_extended;
      } else if (genderLiteral == 'F') {
        return i18nJS.gender_female_extended;
      } else {
        return 'ND';
      }
    },
    additionalInfo(item) {
      let info = [];
      let i = 0;

      if (item.id_mpi) {
        info.push({ id: i++, message: i18nJS.id_mpi + ': ' + item.id_mpi });
      }

      if (item.id_aur) {
        info.push({ id: i++, message: i18nJS.id_aur + ': ' + item.id_aur });
      }

      if (item.gender) {
        info.push({ id: i++, message: i18nJS.gender + ': ' + this.genderExtended(item.gender) });
      }

      if (item.birthplace) {
        info.push({ id: i++, message: i18nJS.birthplace + ': ' + item.birthplace.city + ' (' + item.birthplace.prov + ')' });
      }

      if (item.residence) {
        info.push({
          id: i++,
          message:
            i18nJS.residente_a +
            ': ' +
            item.residence.street +
            ', ' +
            item.residence.city +
            ' (' +
            item.residence.prov +
            '), ' +
            item.residence.cap,
        });
        info.push({ id: i++, message: i18nJS.citizenship + ': ' + item.residence.country + ' - ' + item.residence.country_alpha6 });
      }

      if (item.domicile) {
        info.push({
          id: i++,
          message:
            i18nJS.domiciliato_a +
            ': ' +
            item.domicile.street +
            ', ' +
            item.domicile.city +
            ' (' +
            item.domicile.prov +
            '), ' +
            item.domicile.cap,
        });
      }

      if (item.phone) {
        info.push({ id: i++, message: i18nJS.phone + ': ' + item.phone });
      }

      if (item.email) {
        info.push({ id: i++, message: i18nJS.email + ': ' + item.email });
      }

      if (item.residence_asl) {
        info.push({ id: i++, message: i18nJS.residenza_asl + ': ' + item.residence_asl.asl_cod + ' - ' + item.residence_asl.district_cod });
      }

      if (item.codsan) {
        info.push({ id: i++, message: i18nJS.codsan + ': ' + item.codsan });
      }

      return info;
    },
  },
};
</script>

<style lang="scss">
#adt_search_modal {
  .modal-body {
    min-height: 500px !important;
  }

  .select2.select2-container {
    width: 400px !important;
  }

  input[type='text'] {
    width: 250px;
  }

  input[type='text'].hasDatepicker {
    width: 90px;
  }
}
</style>

<style lang="scss" scoped>
input {
  margin-bottom: 0;
}

#span_search_form {
  position: fixed;
}

#span_search_results {
  position: relative;
  margin-left: 400px;
  height: 400px;
  overflow: auto;
}

#search_results {
  width: 600px;
}

th {
  top: -2px;
  z-index: 100;
}

td {
  vertical-align: middle;
}

.dropdown-content {
  display: none;
  position: absolute;
  margin-top: 10px;
  background-color: #f1f1f1;
  min-width: 400px;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  z-index: 1;
}

.dropdown {
  position: relative;
  display: inline-block;

  &:hover .dropdown-content {
    display: block;
  }
}

.dropbtn {
  padding: 10px;
}

.dropdown-content a:hover {
  background-color: #ddd;
}

.input-checkbox-auto {
  display: inline-block;

  padding: 0 12.5px 0 0;

  input {
    margin: 0 0 3px 0;
  }
}

.section {
  margin: 15px 0;
}

.actionsContainer {
  margin-top: 15px;
  margin-bottom: 15px;
}

.infoContainer {
  margin-top: 20px;
  width: 100%;

  .info-title {
    width: 100%;
  }

  .m_top {
    margin-top: 10px;
  }

  .wrapper {
    display: flex;

    .extended {
      width: 100%;
    }

    .choser,
    .viewer {
      width: 50%;
    }

    .choser {
      flex: 1;
    }

    .viewer {
      flex: 1;
      padding: 5px 25px;

      dl {
        margin: 0;
      }
    }
  }
}

#spinnerwrap {
  z-index: 1100;
}
</style>
